import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { TextField, TextareaField, ToggleField } from '../../../components/orizon-ui/FormField';
import { gruCrea, gruAggiorna } from '../../../lib/services/gruService';
import FormModal, { useFormSalva, useSporco } from '../../../components/gru/FormModal';
import SezioneConfig, { colonnaAttivo } from './SezioneConfig';
import { slugify, CHIAVE_VALIDA, vuoto, interoOrdine } from '../utils';

// ─── Magazzini ───────────────────────────────────────────────────────────────

const FormMagazzino = ({ record, onClose, onSaved }) => {
  const modifica = Boolean(record);
  const [label, setLabelState] = useState(record?.label ?? '');
  const [key, setKeyState] = useState(record?.key ?? '');
  const [keyToccata, setKeyToccata] = useState(Boolean(record));
  const [ordine, setOrdine] = useState(String(record?.ordine ?? 0));
  const [attivo, setAttivo] = useState(record?.attivo ?? true);
  const { salvando, errori, impostaErrori, pulisci, salva } = useFormSalva();
  const sporco = useSporco({ label, key, ordine, attivo });

  const setLabel = (v) => {
    setLabelState(v);
    pulisci('label');
    if (!modifica && !keyToccata) setKeyState(slugify(v));
  };
  const setKey = (v) => {
    setKeyToccata(true);
    setKeyState(v);
    pulisci('key');
  };

  const invia = async () => {
    const nuovi = {};
    if (vuoto(label)) nuovi.label = 'Obbligatorio';
    if (!modifica) {
      if (vuoto(key)) nuovi.key = 'Obbligatorio';
      else if (!CHIAVE_VALIDA.test(key)) nuovi.key = 'Solo minuscole, numeri e _';
    }
    const n = interoOrdine(ordine);
    if (Number.isNaN(n)) nuovi.ordine = 'Numero intero';
    impostaErrori(nuovi);
    if (Object.keys(nuovi).length) return;

    const payload = { label: label.trim(), ordine: n, attivo, ...(modifica ? {} : { key }) };
    await salva(
      () => (modifica ? gruAggiorna('magazzini', record.id, payload) : gruCrea('magazzini', payload)),
      modifica ? 'Magazzino aggiornato.' : 'Magazzino creato.',
      onSaved,
    );
  };

  return (
    <FormModal titolo={modifica ? 'Modifica magazzino' : 'Nuovo magazzino'} etichetta="Magazzini · Magazzino" larghezza="md" sporco={sporco} salvando={salvando} onClose={onClose} onSubmit={invia}>
      <TextField label="Nome" name="label" value={label} onChange={setLabel} obbligatorio errore={errori.label} autoFocus placeholder="es. Magazzino centrale" />
      <TextField
        label="Chiave" name="key" value={key} onChange={setKey}
        obbligatorio={!modifica} disabilitato={modifica} errore={errori.key} mono
        suggerimento={modifica ? 'La chiave non è modificabile.' : 'Identificativo interno: minuscole, numeri e _.'}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <TextField label="Ordine" name="ordine" type="number" value={ordine} onChange={(v) => { setOrdine(v); pulisci('ordine'); }} errore={errori.ordine} />
      </div>
      <ToggleField label="Attivo" sublabel={attivo ? 'Selezionabile' : 'Non selezionabile'} checked={attivo} onChange={setAttivo} />
    </FormModal>
  );
};

const COLONNE_MAGAZZINI = [
  { key: 'ordine', label: 'Ordine', width: 80, align: 'center', render: (r) => <span className="text-[12px] font-bold tabular-nums text-muted-foreground">{r.ordine}</span> },
  { key: 'label', label: 'Nome', width: 200, render: (r) => <span className="text-[13px] font-black uppercase tracking-tight text-foreground/80">{r.label}</span> },
  { key: 'key', label: 'Chiave', width: 150, render: (r) => <span className="font-mono text-[12px] font-bold text-muted-foreground">{r.key}</span> },
  colonnaAttivo,
];

// ─── Ubicazioni ──────────────────────────────────────────────────────────────

const FormUbicazione = ({ record, filtri, onClose, onSaved }) => {
  const modifica = Boolean(record);
  const magazzinoId = filtri?.magazzino_id;
  const [codice, setCodice] = useState(record?.codice ?? '');
  const [descrizione, setDescrizione] = useState(record?.descrizione ?? '');
  const [attivo, setAttivo] = useState(record?.attivo ?? true);
  const { salvando, errori, impostaErrori, pulisci, salva } = useFormSalva();
  const sporco = useSporco({ codice, descrizione, attivo });

  const invia = async () => {
    const nuovi = {};
    if (vuoto(codice)) nuovi.codice = 'Obbligatorio';
    impostaErrori(nuovi);
    if (Object.keys(nuovi).length) return;

    const payload = {
      codice: codice.trim(),
      descrizione: vuoto(descrizione) ? null : descrizione.trim(),
      attivo,
      ...(modifica ? {} : { magazzino_id: magazzinoId }),
    };
    await salva(
      () => (modifica ? gruAggiorna('ubicazioni', record.id, payload) : gruCrea('ubicazioni', payload)),
      modifica ? 'Ubicazione aggiornata.' : 'Ubicazione creata.',
      onSaved,
    );
  };

  return (
    <FormModal titolo={modifica ? 'Modifica ubicazione' : 'Nuova ubicazione'} etichetta="Magazzini · Ubicazione" larghezza="md" sporco={sporco} salvando={salvando} onClose={onClose} onSubmit={invia}>
      <TextField label="Codice" name="codice" value={codice} onChange={(v) => { setCodice(v); pulisci('codice'); }} obbligatorio errore={errori.codice} mono autoFocus placeholder="es. A-03-2" />
      <TextareaField label="Descrizione" name="descrizione" value={descrizione} onChange={setDescrizione} errore={errori.descrizione} />
      <ToggleField label="Attiva" sublabel={attivo ? 'Selezionabile' : 'Non selezionabile'} checked={attivo} onChange={setAttivo} />
    </FormModal>
  );
};

const COLONNE_UBICAZIONI = [
  { key: 'codice', label: 'Codice', width: 140, render: (r) => <span className="font-mono text-[12px] font-bold text-foreground/80">{r.codice}</span> },
  { key: 'descrizione', label: 'Descrizione', width: 260, render: (r) => <span className="text-[12px] font-medium text-muted-foreground">{r.descrizione || '---'}</span> },
  colonnaAttivo,
];

// ─── Tab: master (magazzini) / dettaglio (ubicazioni) ────────────────────────

const TabMagazzini = () => {
  const [selezionato, setSelezionato] = useState(null); // riga magazzino

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
      <SezioneConfig
        entita="magazzini"
        titolo="Magazzini"
        descrizione="Seleziona un magazzino per vederne le ubicazioni"
        nuovoLabel="Nuovo magazzino"
        colonne={COLONNE_MAGAZZINI}
        ordineIniziale={{ key: 'ordine', direction: 'asc' }}
        FormComponent={FormMagazzino}
        onRigaClick={setSelezionato}
        rigaSelezionataId={selezionato?.id}
        compatta
        messaggioVuoto="Nessun magazzino configurato"
      />

      {selezionato ? (
        <SezioneConfig
          key={selezionato.id}
          entita="ubicazioni"
          titolo={`Ubicazioni — ${selezionato.label}`}
          descrizione="Scaffali, ripiani e posizioni del magazzino"
          nuovoLabel="Nuova ubicazione"
          colonne={COLONNE_UBICAZIONI}
          ordineIniziale={{ key: 'codice', direction: 'asc' }}
          filtri={{ magazzino_id: selezionato.id }}
          FormComponent={FormUbicazione}
          labelRiga={(r) => r.codice}
          compatta
          messaggioVuoto="Nessuna ubicazione in questo magazzino"
        />
      ) : (
        <div className="flex flex-col items-center justify-center gap-3 py-24 border-2 border-dashed border-border/40 rounded-3xl bg-muted/5">
          <Icon name="MapPin" size={40} className="text-muted-foreground/20" />
          <span className="text-sm font-bold text-muted-foreground/40 uppercase tracking-widest text-center px-6">
            Seleziona un magazzino per gestirne le ubicazioni
          </span>
        </div>
      )}
    </div>
  );
};

export default TabMagazzini;
